import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AuthLoadingView } from 'shared-ui/lib/components/auth/AuthLoadingView';
import { Details } from 'shared-ui/lib/components/Details';
import { Image } from 'shared-ui/lib/components/Image';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { useParams } from 'shared-ui/lib/utils/use-params';
import { vehicleColumns } from '../../columns/vehicle.columns';
import { PaymentIcon } from '../../components/icons/PaymentIcon';
import { AddIconButton } from '../../components/scenes/vehicle-show-scene/AddIconButton';
import { MaintenanceListItem } from '../../components/scenes/vehicle-show-scene/maintenance-list-item/MaintenanceListItem';
import { ReminderListItem } from '../../components/scenes/vehicle-show-scene/reminder-list-item/ReminderListItem';
import { ServiceCallInviteModal } from '../../components/scenes/vehicle-show-scene/ServiceCallInviteModal';
import { routesConfig } from '../../config/routes.config';
import { VehicleAttachmentsModal } from '../../components/scenes/vehicle-show-scene/VehicleAttachmentsModal';
import { useVehicleShowQuery } from '../../generated/graphql';
import { useCan } from '../../hooks/use-can';

type VehicleAction = {
  key: string;
  label: string;
  icon: JSX.Element;
  onClick?: () => void;
  to?: string;
};

export const VehicleShowScene = () => {
  const { t } = useTranslation('VehicleShowScene');
  const { hasServiceCallAccess, canEditContent } = useCan();
  const [showDetails, setShowDetails] = useState(false);
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const columns = [
    vehicleColumns.name,
    vehicleColumns.registerNumber,
    vehicleColumns.brand,
    vehicleColumns.model,
    vehicleColumns.token,
    vehicleColumns.mileage,
    vehicleColumns.VIN,
    vehicleColumns.runningHours,
    vehicleColumns.modelYear,
    vehicleColumns.commissioning,
    vehicleColumns.warranty,
    vehicleColumns.type,
    vehicleColumns.description,
  ];

  const { id } = useParams<{ id: string }>();
  const { data, loading, refetch } = useVehicleShowQuery(setupQueryConfig({ id }));

  if (loading || !data) return <AuthLoadingView />;

  const { vehicle } = data;

  const actions: VehicleAction[] = [
    {
      key: 'pay',
      label: t('actionPay'),
      to: routesConfig.VEHICLES.CHOOSE_PAYMENT.replace(':id', vehicle.id),
      icon: <PaymentIcon className="h-5 w-5 mr-2 text-primary" />,
    },
  ];
  if (hasServiceCallAccess && canEditContent && data.canSendPartnerServiceCall) {
    actions.push({
      key: 'serviceCall',
      label: t('actionServiceCall'),
      onClick: () => {
        setIsActionsOpen(false);
        setIsInviteOpen(true);
      },
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 mr-2 text-primary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
      ),
    });
  }

  const menuButtonLabel = actions.length === 1 ? actions[0].label : t('actions');

  const emptyRemainder = (
    <div className="text-sm font-medium text-gray-color mt-2">
      <p>{t('emptyRemainder')}</p>
      <Link
        to={routesConfig.REMINDER.CREATE.replace(':vehicleId', id)}
        className="text-primary cursor-pointer font-bold underline"
      >
        <p className="mt-2">{t('emptyRemainderAddNew')}</p>
      </Link>
    </div>
  );

  const emptyServiceEvent = (
    <div className="text-sm font-medium text-gray-color mt-2">
      <p>{t('emptyServiceEvent')}</p>
      <Link
        to={routesConfig.SERVICE_EVENTS.CREATE.replace(':vehicleId', id)}
        className="text-primary cursor-pointer font-bold underline"
      >
        <p className="mt-2">{t('emptyServiceEventAddNew')}</p>
      </Link>
    </div>
  );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3 mt-1 sm:mt-0">
      <div>
        <div className="bg-white py-4 px-3 shadow rounded-lg">
          <div className="grid grid-cols-4 items-center">
            <h2 className="text-lg col-span-2 leading-6 font-bold text-primary">
              {t('vehicleInformation')}
            </h2>
            {actions.length > 0 ? (
              <div className="flex justify-end col-span-2">
                <div className="relative inline-block text-left">
                  <button
                    type="button"
                    onClick={() => setIsActionsOpen(!isActionsOpen)}
                    className="inline-flex justify-center items-center w-full rounded-md border border-primary shadow-sm pl-3 pr-2 py-2 bg-white text-sm font-medium text-primary hover:bg-secondary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                  >
                    <span>{menuButtonLabel}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 ml-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {isActionsOpen ? (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1">
                        {actions.map((action) =>
                          action.to ? (
                            <Link
                              key={action.key}
                              to={action.to}
                              onClick={() => setIsActionsOpen(false)}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              {action.icon}
                              <span>{action.label}</span>
                            </Link>
                          ) : (
                            <button
                              key={action.key}
                              type="button"
                              onClick={action.onClick}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              {action.icon}
                              <span>{action.label}</span>
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
          <div className="max-w-screen-lg mt-6 mx-auto">
            <Image src={vehicle.photo} w={500} className="mx-auto rounded-md" />
          </div>
          <div className="mt-4 flex items-center space-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-primary flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            <button
              type="button"
              onClick={() => setIsAttachmentsOpen(true)}
              className="text-primary hover:text-secondary text-sm font-medium underline"
            >
              {t('attachmentsLink', { count: vehicle.attachments.length })}
            </button>
          </div>
          <VehicleAttachmentsModal
            isOpen={isAttachmentsOpen}
            onClose={() => setIsAttachmentsOpen(false)}
            vehicleId={vehicle.id}
            attachments={vehicle.attachments}
            hasActiveLicense={vehicle.hasActiveLicense}
            onRefetch={() => {
              refetch();
            }}
          />
          <ServiceCallInviteModal
            isOpen={isInviteOpen}
            vehicle={vehicle}
            onClose={() => setIsInviteOpen(false)}
            onSuccess={() => {
              refetch();
            }}
          />
          <div className="mt-6">
            <Details columns={columns} data={vehicle} showMore={showDetails ? undefined : 4} />
            <button
              className="text-secondary cursor-pointer"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? t('hideDetails') : t('showDetails')}
            </button>
          </div>
        </div>
      </div>

      <div>
        <div className="bg-white py-4 px-3 shadow rounded-lg">
          <div className="grid grid-cols-3 items-center">
            <h2 className="text-lg col-span-2 leading-6 font-bold text-primary">{t('reminder')}</h2>
            <div className="flex justify-end">
              <AddIconButton to={routesConfig.REMINDER.CREATE.replace(':vehicleId', id)} />
            </div>
          </div>

          {data.vehicle.reminders.length === 0
            ? emptyRemainder
            : data.vehicle.reminders.map((reminder) => (
                <ReminderListItem key={reminder.id} reminder={reminder} vehicleId={vehicle.id} />
              ))}
        </div>
      </div>

      <div>
        <div className="bg-white py-4 px-3 shadow rounded-lg">
          <div className="grid grid-cols-3 items-center">
            <h2 className="text-lg col-span-2 leading-6 font-bold text-primary">
              {t('serviceEvent')}
            </h2>
            <div className="flex justify-end">
              <AddIconButton
                to={routesConfig.SERVICE_EVENTS.CREATE.replace(':vehicleId', vehicle.id)}
              />
            </div>
          </div>
          {data.vehicle.serviceEvents.length === 0
            ? emptyServiceEvent
            : data.vehicle.serviceEvents.map((serviceEvent) => (
                <MaintenanceListItem
                  key={serviceEvent.id}
                  vehicleId={vehicle.id}
                  serviceEvent={serviceEvent}
                />
              ))}
        </div>
      </div>
    </div>
  );
};
